import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

import reportsService from "api/reportsService";
import {
  IActivityReportResponse,
  ICasesReportResponse,
  IIncomingReportResponse,
  IOutgoingReportResponse,
  IOverdueReportResponse,
  IReportStructure,
} from "dto/Reports/IReports";
import { ENotificationType, notification } from "utils/notification";
import { printRegister } from "utils/printRegister";
import ReportView from "./ReportView";
import { ReportSeries, SERIES_COLORS, SeriesValues, buildTree, flatten } from "./reportModel";
import styles from "./styles.module.scss";

/**
 * Raporty pracy urzędu — port modułu „Raporty" z Magistratu (Kwantum.Reports.UI):
 *  - incoming  — pisma przychodzące (włączone do teczek / przekazane / otrzymane /
 *                w korespondencji / anulowane) per osoba,
 *  - outgoing  — pisma wychodzące per rodzaj (forma doręczenia, dokument wewnętrzny),
 *  - cases     — stan teczek (utworzone / otrzymane / przekazane / zamknięte, w toku na
 *                początek i koniec okresu),
 *  - overdue   — przeterminowane czynności na dzień + teczki w toku, szczegóły per osoba,
 *  - activity  — aktywność pracownika (wpływ korespondencji / pisma wychodzące / stan teczek).
 * Okres domyślnie 30 dni (PeriodicalReportVM), „przeterminowane" — dzień (DateReportVM).
 */

type ReportMode = "incoming" | "outgoing" | "cases" | "overdue" | "activity";

const REPORT_TITLES: Record<ReportMode, string> = {
  incoming: "Pisma przychodzące",
  outgoing: "Pisma wychodzące",
  cases: "Stan teczek",
  overdue: "Przeterminowane",
  activity: "Aktywność pracownika",
};

const VIEW_TO_MODE: Record<string, ReportMode> = {
  incoming: "incoming",
  outgoing: "outgoing",
  cases: "cases",
  overdue: "overdue",
  activity: "activity",
};

const pad2 = (n: number) => String(n).padStart(2, "0");
const toISO = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const plDate = (iso: string) => (iso ? moment(iso).format("DD.MM.YYYY") : "");

const INCOMING_SERIES: ReportSeries[] = [
  { key: "Passed", label: "Przekazano", color: SERIES_COLORS[0] },
  { key: "AttachedToCases", label: "Włączono do teczek", color: SERIES_COLORS[1] },
  { key: "Pending", label: "Pozostaje w korespondencji", color: SERIES_COLORS[2] },
  { key: "Received", label: "Otrzymano", color: SERIES_COLORS[4] },
  { key: "Canceled", label: "Anulowano", color: SERIES_COLORS[3] },
];

const CASES_SERIES: ReportSeries[] = [
  { key: "Created", label: "Utworzone", color: SERIES_COLORS[0] },
  { key: "Received", label: "Otrzymane", color: SERIES_COLORS[4] },
  { key: "Closed", label: "Zamknięte", color: SERIES_COLORS[1] },
  { key: "Passed", label: "Przekazane", color: SERIES_COLORS[2] },
  { key: "PendingAtStart", label: "W toku na początek", color: SERIES_COLORS[6] },
  { key: "PendingAtEnd", label: "W toku na koniec", color: SERIES_COLORS[7] },
];

const OVERDUE_SERIES: ReportSeries[] = [
  { key: "Overdue", label: "Przeterminowane czynności", color: SERIES_COLORS[3] },
  { key: "PendingCases", label: "Teczki w toku", color: SERIES_COLORS[0] },
];

const ACTIVITY_SERIES: ReportSeries[] = [
  { key: "IncomingCorrespondence", label: "Wpływ korespondencji", color: SERIES_COLORS[0] },
  { key: "OutgoingCorrespondence", label: "Pisma wychodzące", color: SERIES_COLORS[1] },
  { key: "Cases", label: "Stan teczek", color: SERIES_COLORS[2] },
];

const rowsToMap = <T extends { UserId: string }>(rows: T[] | undefined, pick: (r: T) => SeriesValues) => {
  const m = new Map<string, SeriesValues>();
  (rows ?? []).forEach((r) => m.set(r.UserId, pick(r)));
  return m;
};

const ReportsModule = () => {
  const { view } = useParams();
  const mode: ReportMode = VIEW_TO_MODE[view ?? ""] ?? "incoming";

  const today = new Date();
  const [from, setFrom] = useState(toISO(new Date(today.getTime() - 29 * 86400000)));
  const [to, setTo] = useState(toISO(today));
  const [date, setDate] = useState(toISO(today));

  // Natywne pole daty w trakcie edycji daje "" — nie strzelamy wtedy do BE (400), a pusty
  // lub odwrócony zakres blokuje zapytanie do czasu poprawienia.
  const periodValid = !!from && !!to && from <= to;
  const dateValid = !!date;
  const period = { PeriodStart: from, PeriodEnd: to };
  const incomingQ = reportsService.useIncomingReport(period, mode === "incoming" && periodValid);
  const outgoingQ = reportsService.useOutgoingReport(period, mode === "outgoing" && periodValid);
  const casesQ = reportsService.useCasesReport(period, mode === "cases" && periodValid);
  const overdueQ = reportsService.useOverdueReport({ Date: date }, mode === "overdue" && dateValid);
  const activityQ = reportsService.useActivityReport(period, mode === "activity" && periodValid);

  // Serie, struktura i wartości per osoba dla bieżącego trybu.
  const { series, structure, valuesByUser, loading } = useMemo(() => {
    switch (mode) {
      case "incoming": {
        const d: IIncomingReportResponse | undefined = incomingQ.data?.data;
        return {
          series: INCOMING_SERIES,
          structure: d?.Structure,
          valuesByUser: rowsToMap(d?.Rows, (r) => ({
            Passed: r.Passed, AttachedToCases: r.AttachedToCases, Pending: r.Pending, Received: r.Received, Canceled: r.Canceled,
          })),
          loading: incomingQ.isLoading,
        };
      }
      case "outgoing": {
        const d: IOutgoingReportResponse | undefined = outgoingQ.data?.data;
        const types = d?.Types ?? [];
        return {
          series: types.map((t, i) => ({ key: t, label: t, color: SERIES_COLORS[i % SERIES_COLORS.length] })),
          structure: d?.Structure,
          valuesByUser: rowsToMap(d?.Rows, (r) => ({ ...r.ByType })),
          loading: outgoingQ.isLoading,
        };
      }
      case "cases": {
        const d: ICasesReportResponse | undefined = casesQ.data?.data;
        return {
          series: CASES_SERIES,
          structure: d?.Structure,
          valuesByUser: rowsToMap(d?.Rows, (r) => ({
            Created: r.Created, Received: r.Received, Closed: r.Closed, Passed: r.Passed, PendingAtStart: r.PendingAtStart, PendingAtEnd: r.PendingAtEnd,
          })),
          loading: casesQ.isLoading,
        };
      }
      case "overdue": {
        const d: IOverdueReportResponse | undefined = overdueQ.data?.data;
        return {
          series: OVERDUE_SERIES,
          structure: d?.Structure,
          valuesByUser: rowsToMap(d?.Rows, (r) => ({ Overdue: r.Overdue, PendingCases: r.PendingCases })),
          loading: overdueQ.isLoading,
        };
      }
      default: {
        const d: IActivityReportResponse | undefined = activityQ.data?.data;
        return {
          series: ACTIVITY_SERIES,
          structure: d?.Structure,
          valuesByUser: rowsToMap(d?.Rows, (r) => ({
            IncomingCorrespondence: r.IncomingCorrespondence, OutgoingCorrespondence: r.OutgoingCorrespondence, Cases: r.Cases,
          })),
          loading: activityQ.isLoading,
        };
      }
    }
  }, [mode, incomingQ.data, outgoingQ.data, casesQ.data, overdueQ.data, activityQ.data,
      incomingQ.isLoading, outgoingQ.isLoading, casesQ.isLoading, overdueQ.isLoading, activityQ.isLoading]);

  const periodLabel = mode === "overdue" ? `Na dzień: ${plDate(date)}` : `Okres: ${plDate(from)} – ${plDate(to)}`;

  // Szczegóły przeterminowanych czynności osoby (OverdueCasesDetailedReportVM).
  const renderOverdueDetails = (userId: string) => {
    const d = overdueQ.data?.data;
    const row = d?.Rows.find((r) => r.UserId === userId);
    const person = d?.Structure.People.find((p) => p.UserId === userId);
    if (!row) return null;
    return (
      <>
        <div className={styles.detailsTitle}>
          {person?.Name ?? "Osoba"} — przeterminowane czynności: {row.Overdue}, teczki w toku: {row.PendingCases}
        </div>
        {row.Items.length === 0 ? (
          <div className={styles.empty}>Brak przeterminowanych czynności.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.left}>Czynność</th>
                <th className={styles.left}>Teczka</th>
                <th>Termin</th>
                <th>Dni po terminie</th>
              </tr>
            </thead>
            <tbody>
              {row.Items.map((i) => (
                <tr key={i.TaskId}>
                  <td className={styles.left}>{i.Title || "—"}</td>
                  <td className={styles.left}>{i.CaseSignature || "—"}</td>
                  <td>{plDate(i.DueDate)}</td>
                  <td className={styles.strong}>{i.Days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  };

  // Wydruk: tabela osób × serie (wszystkie osoby ze struktury) — ten sam mechanizm co rejestry.
  const doPrint = () => {
    if (!structure) return;
    const nodes = flatten(buildTree(structure as IReportStructure, series.map((s) => s.key), valuesByUser)).filter((n) => n.isUser);
    const ok = printRegister({
      title: `Raport: ${REPORT_TITLES[mode]}`,
      subtitle: periodLabel,
      columns: [
        { header: "Osoba", value: (n) => n.name, width: "26%" },
        ...series.map((s) => ({ header: s.label, value: (n: typeof nodes[number]) => n.values[s.key] ?? 0, align: "right" as const })),
        { header: "Razem", value: (n) => n.total, align: "right" as const, width: "9%" },
      ],
      rows: nodes,
      landscape: series.length > 4,
    });
    if (!ok)
      notification({
        type: ENotificationType.ERROR,
        title: "Nie udało się otworzyć wydruku",
        text: "Zezwól na wyskakujące okna dla tej strony.",
      });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.title}>{REPORT_TITLES[mode]}</span>
          {mode === "overdue" ? (
            <TextField
              label="Na dzień"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          ) : (
            <>
              <TextField
                label="Okres od"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <TextField
                label="Okres do"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </>
          )}
          {loading && <span className={styles.loadingHint}>Generowanie…</span>}
          {(mode === "overdue" ? !dateValid : !periodValid) && (
            <span className={styles.invalidHint}>
              {mode === "overdue" ? "Podaj dzień raportu." : "Podaj poprawny okres (od ≤ do)."}
            </span>
          )}
        </div>
        <button className={styles.printBtn} onClick={doPrint} disabled={!structure}>
          <FontAwesomeIcon icon={faPrint} /> Drukuj
        </button>
      </div>

      <ReportView
        structure={structure}
        series={series}
        valuesByUser={valuesByUser}
        loading={loading}
        renderDetails={mode === "overdue" ? renderOverdueDetails : undefined}
      />
    </div>
  );
};

export default ReportsModule;
